import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  TrophyIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  ChartBarIcon,
  SparklesIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';
import { createSafeHtml, containsHtml } from '../utils/htmlUtils';
import { QuestionType } from '../services/quizService';

interface QuizResultsProps {
  // This will come from the quiz submission
}

interface QuizResult {
  quiz: {
    _id: string;
    title: string;
    description?: string;
    questions: Array<{
      id: string;
      question: string;
      questionType?: QuestionType;
      correctAnswer: string;
      explanation?: string;
      blankText?: string;
      expectedKeyPoints?: string[];
    }>;
  };
  attempt: {
    _id: string;
    score: number;
    totalQuestions: number;
    correctAnswers: number;
    timeSpent: number;
    answers: Array<{
      questionId: string;
      selectedAnswer: string;
      isCorrect: boolean;
      aiScore?: number;
      aiFeedback?: string;
      keyPointsMatched?: string[];
      keyPointsMissed?: string[];
    }>;
  };
  summary: {
    score: number;
    totalQuestions: number;
    correctAnswers: number;
    incorrectAnswers: number;
    timeSpent: number;
    isNewBestScore: boolean;
  };
}

// Helper para obter tipo de questão
const getQuestionType = (questionType?: QuestionType): QuestionType => {
  return questionType || 'multiple_choice';
};

// Helper para obter label do tipo de questão
const getQuestionTypeLabel = (type: QuestionType): string => {
  switch (type) {
    case 'multiple_choice': return 'Múltipla Escolha';
    case 'true_false': return 'Verdadeiro ou Falso';
    case 'fill_blank': return 'Preencher Lacuna';
    case 'essay': return 'Dissertativa';
    default: return 'Questão';
  }
};

const QuizResults: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get result data passed from QuizPlay component
  const result = location.state?.result as QuizResult;

  if (!result) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Resultados não encontrados
          </h2>
          <button
            onClick={() => navigate('/quizzes')}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg"
          >
            Voltar aos Quizzes
          </button>
        </div>
      </div>
    );
  }

  const { quiz, attempt, summary } = result;
  const scorePercentage = Math.round((summary.correctAnswers / summary.totalQuestions) * 100);
  
  const getScoreColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600 dark:text-green-400';
    if (percentage >= 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getScoreBgColor = (percentage: number) => {
    if (percentage >= 80) return 'bg-green-100 dark:bg-green-900/30';
    if (percentage >= 60) return 'bg-yellow-100 dark:bg-yellow-900/30';
    return 'bg-red-100 dark:bg-red-900/30';
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const retryQuiz = () => {
    navigate(`/quiz/${id}`);
  };

  const goBackToQuizzes = () => {
    navigate('/quizzes');
  };

  return (
    <div>
      {/* Header */}
      <div className="shadow-sm">
        <div className="max-w-5xl mx-auto px-1 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center">
              <button
                onClick={goBackToQuizzes}
                className="mr-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                  Resultados do Quiz
                </h1>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                  {quiz.title}
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-2 sm:gap-3 px-2 sm:px-0">
              <button
                onClick={retryQuiz}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Tentar Novamente
              </button>
              <button
                onClick={goBackToQuizzes}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700"
              >
                Voltar aos Quizzes
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-1 sm:px-6 lg:px-8 py-8">
        {/* Score Summary */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 border-l-[1px] border-r-[1px] sm:border-l sm:border-r mb-8">
          <div className="p-4 sm:p-8 text-center">
            <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full mb-6 ${getScoreBgColor(scorePercentage)}`}>
              {scorePercentage >= 80 ? (
                <TrophyIcon className={`h-12 w-12 ${getScoreColor(scorePercentage)}`} />
              ) : scorePercentage >= 60 ? (
                <CheckCircleIcon className={`h-12 w-12 ${getScoreColor(scorePercentage)}`} />
              ) : (
                <XCircleIcon className={`h-12 w-12 ${getScoreColor(scorePercentage)}`} />
              )}
            </div>
            
            <h2 className={`text-4xl font-bold mb-2 ${getScoreColor(scorePercentage)}`}>
              {scorePercentage}%
            </h2>
            
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-6">
              {scorePercentage >= 80 ? 'Excelente!' : 
               scorePercentage >= 60 ? 'Bom trabalho!' : 
               'Continue praticando!'}
            </p>

            {summary.isNewBestScore && (
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 text-sm font-medium mb-4">
                <TrophyIcon className="h-4 w-4 mr-2" />
                Nova melhor pontuação!
              </div>
            )}

            <div className="grid grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {summary.correctAnswers}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Corretas
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {summary.incorrectAnswers}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Incorretas
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {formatTime(summary.timeSpent)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Tempo Gasto
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Results */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 border-l-[1px] border-r-[1px] sm:border-l sm:border-r">
          <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
              <ChartBarIcon className="h-5 w-5 mr-2" />
              Revisão das Perguntas
            </h3>
          </div>
          
          <div className="p-4 sm:p-6">
            <div className="space-y-4 sm:space-y-6">
              {quiz.questions.map((question, index) => {
                const userAnswer = attempt.answers.find(a => a.questionId === question.id);
                const isCorrect = userAnswer?.isCorrect || false;
                const questionType = getQuestionType(question.questionType);
                const hasAiFeedback = userAnswer?.aiFeedback || userAnswer?.aiScore !== undefined;
                
                return (
                  <div
                    key={question.id}
                    className={`p-4 rounded-lg border-2 ${
                      questionType === 'essay' && hasAiFeedback
                        ? userAnswer?.aiScore && userAnswer.aiScore >= 60
                          ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
                          : 'border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20'
                        : isCorrect
                          ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
                          : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
                    }`}
                  >
                    <div className="flex items-start">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-4 ${
                        questionType === 'essay' && hasAiFeedback
                          ? userAnswer?.aiScore && userAnswer.aiScore >= 60
                            ? 'bg-green-100 dark:bg-green-900/30'
                            : 'bg-yellow-100 dark:bg-yellow-900/30'
                          : isCorrect
                            ? 'bg-green-100 dark:bg-green-900/30'
                            : 'bg-red-100 dark:bg-red-900/30'
                      }`}>
                        {questionType === 'essay' && hasAiFeedback ? (
                          <SparklesIcon className={`h-5 w-5 ${
                            userAnswer?.aiScore && userAnswer.aiScore >= 60
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-yellow-600 dark:text-yellow-400'
                          }`} />
                        ) : isCorrect ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                        ) : (
                          <XCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
                        )}
                      </div>
                      
                      <div className="flex-1">
                        {/* Header com tipo de questão */}
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            questionType === 'multiple_choice' 
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                              : questionType === 'true_false'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : questionType === 'fill_blank'
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                              : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                          }`}>
                            {getQuestionTypeLabel(questionType)}
                          </span>
                          {hasAiFeedback && userAnswer?.aiScore !== undefined && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              userAnswer.aiScore >= 80 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                : userAnswer.aiScore >= 60
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                            }`}>
                              <SparklesIcon className="h-3 w-3 mr-1" />
                              IA: {userAnswer.aiScore}%
                            </span>
                          )}
                        </div>

                        {containsHtml(question.question) ? (
                          <h4 
                            className="font-medium text-gray-900 dark:text-gray-100 mb-2"
                            dangerouslySetInnerHTML={createSafeHtml(`Pergunta ${index + 1}: ${question.question}`)}
                          />
                        ) : (
                          <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                            Pergunta {index + 1}: {question.question}
                          </h4>
                        )}

                        {/* Texto com lacuna para fill_blank */}
                        {questionType === 'fill_blank' && question.blankText && (
                          <div className="mb-2 p-2 bg-gray-100 dark:bg-gray-700/50 rounded text-sm">
                            {question.blankText}
                          </div>
                        )}
                        
                        <div className="space-y-2 text-sm">
                          <div>
                            <span className="font-medium text-gray-700 dark:text-gray-300">
                              Sua resposta:
                            </span>
                            {questionType === 'essay' ? (
                              <div className="mt-1 p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                  {userAnswer?.selectedAnswer || 'Não respondida'}
                                </p>
                              </div>
                            ) : containsHtml(userAnswer?.selectedAnswer || '') ? (
                              <span 
                                className={`ml-2 ${
                                  isCorrect
                                    ? 'text-green-700 dark:text-green-300'
                                    : 'text-red-700 dark:text-red-300'
                                }`}
                                dangerouslySetInnerHTML={createSafeHtml(userAnswer?.selectedAnswer || 'Não respondida')}
                              />
                            ) : (
                              <span className={`ml-2 ${
                                isCorrect
                                  ? 'text-green-700 dark:text-green-300'
                                  : 'text-red-700 dark:text-red-300'
                              }`}>
                                {userAnswer?.selectedAnswer || 'Não respondida'}
                              </span>
                            )}
                          </div>
                          
                          {/* Resposta correta (não exibir para dissertativas) */}
                          {!isCorrect && questionType !== 'essay' && (
                            <div>
                              <span className="font-medium text-gray-700 dark:text-gray-300">
                                Resposta correta:
                              </span>
                              {containsHtml(question.correctAnswer) ? (
                                <span 
                                  className="ml-2 text-green-700 dark:text-green-300"
                                  dangerouslySetInnerHTML={createSafeHtml(question.correctAnswer)}
                                />
                              ) : (
                                <span className="ml-2 text-green-700 dark:text-green-300">
                                  {question.correctAnswer}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Feedback da IA para dissertativas e preenchimento */}
                          {hasAiFeedback && userAnswer?.aiFeedback && (
                            <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                              <div className="flex items-center mb-2">
                                <SparklesIcon className="h-4 w-4 text-purple-600 dark:text-purple-400 mr-2" />
                                <span className="font-medium text-purple-800 dark:text-purple-300">
                                  Avaliação da IA:
                                </span>
                              </div>
                              <p className="text-purple-700 dark:text-purple-300">
                                {userAnswer.aiFeedback}
                              </p>
                            </div>
                          )}

                          {/* Pontos-chave para dissertativas */}
                          {questionType === 'essay' && (userAnswer?.keyPointsMatched?.length || userAnswer?.keyPointsMissed?.length) && (
                            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                              {userAnswer?.keyPointsMatched && userAnswer.keyPointsMatched.length > 0 && (
                                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                  <div className="flex items-center mb-2">
                                    <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400 mr-2" />
                                    <span className="font-medium text-green-800 dark:text-green-300 text-sm">
                                      Pontos Identificados:
                                    </span>
                                  </div>
                                  <ul className="text-sm text-green-700 dark:text-green-300 list-disc list-inside">
                                    {userAnswer.keyPointsMatched.map((point, idx) => (
                                      <li key={idx}>{point}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {userAnswer?.keyPointsMissed && userAnswer.keyPointsMissed.length > 0 && (
                                <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                                  <div className="flex items-center mb-2">
                                    <LightBulbIcon className="h-4 w-4 text-orange-600 dark:text-orange-400 mr-2" />
                                    <span className="font-medium text-orange-800 dark:text-orange-300 text-sm">
                                      Pontos a Melhorar:
                                    </span>
                                  </div>
                                  <ul className="text-sm text-orange-700 dark:text-orange-300 list-disc list-inside">
                                    {userAnswer.keyPointsMissed.map((point, idx) => (
                                      <li key={idx}>{point}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {question.explanation && (
                            <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                              <span className="font-medium text-blue-800 dark:text-blue-300">
                                Explicação:
                              </span>
                              {containsHtml(question.explanation) ? (
                                <p 
                                  className="text-blue-700 dark:text-blue-300 mt-1"
                                  dangerouslySetInnerHTML={createSafeHtml(question.explanation)}
                                />
                              ) : (
                                <p className="text-blue-700 dark:text-blue-300 mt-1">
                                  {question.explanation}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 px-2 sm:px-0">
          <button
            onClick={retryQuiz}
            className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            <ArrowPathIcon className="h-5 w-5 mr-2" />
            Tentar Novamente
          </button>
          <button
            onClick={goBackToQuizzes}
            className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 dark:border-gray-600 text-base font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Voltar aos Quizzes
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuizResults;